import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import type { AdminContact, EventAttendee, ScheduledEvent } from "@tih/shared";
import {
  ApiError,
  adminCheckInAttendee,
  adminCreateEvent,
  adminCreateEventAttendee,
  adminFetchContactsBook,
  adminFetchEventAttendees,
  adminFetchEvents,
  adminFetchFeaturedEvent,
} from "../../api/client";
import { firstAdminPath, hasPermission } from "@tih/shared";
import { RequireAdmin } from "../../components/RequireAuth";
import { useAuth } from "../../context/AuthContext";
import {
  KioskFirstTimePanel,
} from "../../components/check-in/KioskFirstTimePanel";
import {
  KioskReturningPanel,
  type ReturningMode,
} from "../../components/check-in/KioskReturningPanel";
import { KioskSuccessSplash } from "../../components/check-in/KioskSuccessSplash";
import {
  normalizeEmail,
  normalizePersonName,
  phoneDigits,
  pickDefaultCheckInEvent,
} from "../../lib/attendee-directory";
import { eventDisplayTitle } from "../../lib/event-admin-utils";

const THANKS_MS = 4500;

type MainTab = "returning" | "first";
type Splash = { variant: "welcome" | "already"; name: string } | null;

/** Shared shape for tonight attendees (mobile) and address-book contacts (phone). */
type MatchablePerson = {
  name: string;
  email?: string | null;
  mobile?: string | null;
  phone?: string | null;
};

function matchPhoneDigits(person: MatchablePerson): string {
  return phoneDigits(person.mobile ?? person.phone);
}

function findByPhone<T extends MatchablePerson>(rows: T[], digits: string): T | null {
  if (digits.length < 7) return null;
  return (
    rows.find((row) => {
      const d = matchPhoneDigits(row);
      if (d.length < 7) return false;
      return d === digits || d.endsWith(digits) || digits.endsWith(d);
    }) ?? null
  );
}

function findByEmail<T extends MatchablePerson>(rows: T[], email: string): T | null {
  const e = normalizeEmail(email);
  if (!e) return null;
  return rows.find((row) => normalizeEmail(row.email) === e) ?? null;
}

function findByName<T extends MatchablePerson>(rows: T[], name: string): T | null {
  const n = normalizePersonName(name);
  if (n.length < 2) return null;
  const exact = rows.find((row) => normalizePersonName(row.name) === n);
  if (exact) return exact;
  const starts = rows.filter((row) => normalizePersonName(row.name).startsWith(n));
  return starts.length === 1 ? starts[0] : null;
}

function AdminSelfCheckInForm() {
  const { user } = useAuth();

  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [eventId, setEventId] = useState<number | null>(null);
  const [tonight, setTonight] = useState<EventAttendee[]>([]);
  const [contacts, setContacts] = useState<AdminContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [splash, setSplash] = useState<Splash>(null);
  const [noMatch, setNoMatch] = useState(false);

  const [mainTab, setMainTab] = useState<MainTab>("returning");
  const [returningMode, setReturningMode] = useState<ReturningMode>("phone");

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [mobileFreeform, setMobileFreeform] = useState("");

  const selectedEvent = events.find((e) => e.id === eventId) ?? null;
  const canCreateEvents = hasPermission(user, "events");

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, featured] = await Promise.all([
        adminFetchEvents(),
        adminFetchFeaturedEvent(),
      ]);
      let nextRows = rows;
      let nextId = pickDefaultCheckInEvent(rows, featured?.id ?? null);

      // Empty calendar: auto-create a same-day overnight when the signed-in
      // user can write Events. check_in-only hosts cannot POST /events.
      if (nextId == null && canCreateEvents) {
        try {
          const startsAt = new Date();
          const created = await adminCreateEvent({
            startsAt: startsAt.toISOString(),
            title: "Tonight",
          });
          nextRows = [created, ...rows];
          nextId = created.id;
        } catch (createErr) {
          setError(
            createErr instanceof ApiError
              ? createErr.message
              : "Could not create tonight’s overnight — add it in Events.",
          );
        }
      }

      setEvents(nextRows);
      setEventId(nextId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load tonight’s event");
    } finally {
      setLoading(false);
    }
  }, [canCreateEvents]);

  const loadTonight = useCallback(async () => {
    if (eventId == null) return;
    try {
      setTonight(await adminFetchEventAttendees(eventId));
    } catch {
      /* Matching is best-effort; the form still works. */
    }
  }, [eventId]);

  const loadContacts = useCallback(async () => {
    try {
      const payload = await adminFetchContactsBook();
      setContacts(payload.contacts);
    } catch {
      /* Address book is best-effort; tonight matching still works. */
    }
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    void loadTonight();
  }, [loadTonight]);

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    if (!splash) return;
    const id = window.setTimeout(() => resetAll(), THANKS_MS);
    return () => window.clearTimeout(id);
    // resetAll is stable enough via closure; intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splash]);

  function resetAll() {
    setPhone("");
    setEmail("");
    setName("");
    setLocation("");
    setMobileFreeform("");
    setError(null);
    setSplash(null);
    setNoMatch(false);
    setBusy(false);
    setMainTab("returning");
    setReturningMode("phone");
  }

  function clearLookupFeedback() {
    setError(null);
    setNoMatch(false);
  }

  async function finishCheckIn(displayName: string, already: boolean) {
    await loadTonight();
    setPhone("");
    setEmail("");
    setName("");
    setLocation("");
    setMobileFreeform("");
    setNoMatch(false);
    setError(null);
    setSplash({ variant: already ? "already" : "welcome", name: displayName });
  }

  async function checkInExisting(row: EventAttendee) {
    if (row.checkedInAt) {
      await finishCheckIn(row.name, true);
      return;
    }
    await adminCheckInAttendee(row.id);
    await finishCheckIn(row.name, false);
  }

  /** Add a known contact to tonight’s list (checked in), or check in if already there. */
  async function checkInFromContact(contact: AdminContact) {
    if (eventId == null) return;

    const existing =
      (contact.email ? findByEmail(tonight, contact.email) : null) ??
      findByPhone(tonight, phoneDigits(contact.phone));
    if (existing) {
      await checkInExisting(existing);
      return;
    }

    try {
      await adminCreateEventAttendee(eventId, {
        name: contact.name,
        mobile: contact.phone || null,
        email: contact.email || null,
        checkIn: true,
      });
      await finishCheckIn(contact.name, false);
    } catch (err) {
      // Create may race or hit a server-side duplicate — reload and check them in.
      const refreshed = await adminFetchEventAttendees(eventId);
      setTonight(refreshed);
      const dup =
        (contact.email ? findByEmail(refreshed, contact.email) : null) ??
        findByPhone(refreshed, phoneDigits(contact.phone)) ??
        findByName(refreshed, contact.name);
      if (dup) {
        await checkInExisting(dup);
        return;
      }
      throw err;
    }
  }

  function matchTonight(mode: ReturningMode): EventAttendee | null {
    if (mode === "phone") return findByPhone(tonight, phone);
    if (mode === "email") return findByEmail(tonight, email);
    return findByName(tonight, name);
  }

  function matchContact(mode: ReturningMode): AdminContact | null {
    if (mode === "phone") return findByPhone(contacts, phone);
    if (mode === "email") return findByEmail(contacts, email);
    return findByName(contacts, name);
  }

  async function onReturningCheckIn() {
    if (eventId == null || busy) return;
    setBusy(true);
    clearLookupFeedback();
    try {
      // 1. Tonight’s attendees → existing check-in / already-in
      const tonightMatch = matchTonight(returningMode);
      if (tonightMatch) {
        await checkInExisting(tonightMatch);
        return;
      }

      // 2. Full address book (RSVP not required)
      const contactMatch = matchContact(returningMode);
      if (contactMatch) {
        await checkInFromContact(contactMatch);
        return;
      }

      // 3. Unknown → first-time register
      setNoMatch(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not check you in. Please ask a host.");
    } finally {
      setBusy(false);
    }
  }

  async function onFirstTimeSubmit() {
    if (eventId == null || !name.trim() || busy) return;
    setBusy(true);
    clearLookupFeedback();
    const fields = {
      name: name.trim(),
      mobile: (mobileFreeform.trim() || phone).trim(),
      email: email.trim(),
      location: location.trim(),
    };
    try {
      const byEmail = fields.email ? findByEmail(tonight, fields.email) : null;
      const byPhone = phoneDigits(fields.mobile).length >= 7
        ? findByPhone(tonight, phoneDigits(fields.mobile))
        : null;
      const existing = byEmail ?? byPhone;
      if (existing) {
        await checkInExisting(existing);
        return;
      }
      await adminCreateEventAttendee(eventId, {
        name: fields.name,
        mobile: fields.mobile || null,
        email: fields.email || null,
        location: fields.location || null,
        checkIn: true,
      });
      await finishCheckIn(fields.name, false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not check you in. Please ask a host.");
    } finally {
      setBusy(false);
    }
  }

  function goRegisterFromNoMatch() {
    if (returningMode === "phone") {
      setMobileFreeform(phone);
    } else if (returningMode === "email") {
      // email already set
    } else {
      // name already set
    }
    setNoMatch(false);
    setError(null);
    setMainTab("first");
  }

  if (loading) {
    return (
      <div className="kiosk-checkin">
        <div className="kiosk-loading">Loading…</div>
      </div>
    );
  }

  if (splash) {
    return (
      <div className="kiosk-checkin">
        <KioskSuccessSplash
          variant={splash.variant}
          name={splash.name}
          onDismiss={resetAll}
        />
      </div>
    );
  }

  if (eventId == null) {
    return (
      <div className="kiosk-checkin">
        <div className="kiosk-empty-event">
          <div className="sec-eyebrow"><em>self check-in</em></div>
          <h1 className="kiosk-success-title">Add tonight’s overnight in Events first</h1>
          <p className="kiosk-success-lede">
            This kiosk always uses the current overnight. There is no event on the calendar yet
            {canCreateEvents
              ? ", and creating one from here failed — open Events to add tonight’s night."
              : ". Ask a host with Events access to add tonight’s overnight, then reload this page."}
          </p>
          {error && <p className="kiosk-message form-error">{error}</p>}
          {canCreateEvents ? (
            <p>
              <Link className="btn kiosk-btn kiosk-btn-primary" to="/admin/events/new">
                Open Events
              </Link>
            </p>
          ) : (
            <p>
              <Link className="btn kiosk-btn kiosk-btn-primary" to="/admin/events">
                Events calendar
              </Link>
            </p>
          )}
          <p className="kiosk-staff">
            <Link to="/admin/check-in">Hosts: back to the check-in desk</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-checkin">
      <header className="kiosk-header">
        <img src="/assets/logo.png" alt="Together In Heaven" className="kiosk-logo" />
        <div className="kiosk-header-text">
          <div className="sec-eyebrow"><em>self check-in</em></div>
          <h1 className="kiosk-event-title">
            {selectedEvent ? eventDisplayTitle(selectedEvent) : "Welcome"}
          </h1>
        </div>
      </header>

      <div className="kiosk-tabs" role="tablist" aria-label="Check-in mode">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "returning"}
          className={`kiosk-tab${mainTab === "returning" ? " is-active" : ""}`}
          onClick={() => {
            setMainTab("returning");
            clearLookupFeedback();
          }}
          disabled={busy}
        >
          Returning
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === "first"}
          className={`kiosk-tab${mainTab === "first" ? " is-active" : ""}`}
          onClick={() => {
            setMainTab("first");
            clearLookupFeedback();
          }}
          disabled={busy}
        >
          First time
        </button>
      </div>

      {mainTab === "returning" ? (
        <KioskReturningPanel
          mode={returningMode}
          onModeChange={(mode) => {
            setReturningMode(mode);
            clearLookupFeedback();
          }}
          phone={phone}
          onPhoneChange={(v) => {
            setPhone(v);
            clearLookupFeedback();
          }}
          email={email}
          onEmailChange={(v) => {
            setEmail(v);
            clearLookupFeedback();
          }}
          name={name}
          onNameChange={(v) => {
            setName(v);
            clearLookupFeedback();
          }}
          busy={busy}
          disabled={eventId == null}
          error={error}
          noMatch={noMatch}
          onCheckIn={() => void onReturningCheckIn()}
          onGoRegister={goRegisterFromNoMatch}
        />
      ) : (
        <KioskFirstTimePanel
          name={name}
          onNameChange={setName}
          mobile={mobileFreeform}
          onMobileChange={setMobileFreeform}
          email={email}
          onEmailChange={setEmail}
          location={location}
          onLocationChange={setLocation}
          busy={busy}
          disabled={eventId == null}
          error={error}
          onSubmit={() => void onFirstTimeSubmit()}
        />
      )}

      <p className="kiosk-staff">
        <Link to="/admin/check-in">Hosts: back to the check-in desk</Link>
      </p>
    </div>
  );
}

function RequireCheckIn() {
  const { user } = useAuth();
  if (!hasPermission(user, "check_in")) {
    return <Navigate to={firstAdminPath(user)} replace />;
  }
  return <AdminSelfCheckInForm />;
}

export function AdminSelfCheckInPage() {
  return (
    <RequireAdmin>
      <RequireCheckIn />
    </RequireAdmin>
  );
}
